/**
 * TimelineCalendar - Interactive timeline for project management visualization
 * Enhanced with language support for internationalization
 */
class TimelineCalendar {
  /**
   * Create a new TimelineCalendar
   * @param {Object} options - Configuration options
   */
  constructor(options = {}) {
    this._initializeLanguage(options);

    this.options = this._initializeOptions(options);

    this.currentDate = new Date();
    this.currentColor = '#4a6fff';
    this.currentProject = null;
    this.headers = [];
    this.groupHeaderHeight = 0;
    this.timeLineWidth = 0;
    this.isSaving = false;
    this.saveTimeout = null;
    this.events = {};
    this.taskQueue = [];
    this.isProcessingQueue = false;
    this.treeData = null;
    this.isClickToAddMode = false;
    this.currentMode = 'normal';
    this.headerStructure = {
      mainGroups: [],
      subGroups: {}
    };
    this.apiBase = options.apiBase || 'api/index.php/';

    this.uiCallbacks = {
      onShowNotification: options.onShowNotification || this._defaultShowNotification.bind(this),
      onModeChange: options.onModeChange || null,
      onEmptyState: options.onEmptyState || null,
      onDataLoaded: options.onDataLoaded || null
    };

    if (!this.options.filterRange) {
      this.options.filterRange = {
        start: new Date(),
        end: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000)
      };
    } else {
      this.options.filterRange = {
        start: new Date(this.options.filterRange.start),
        end: new Date(this.options.filterRange.end)
      };
    }

    this.dataManager = new DataManager(this);
    this.domRenderer = new DOMRenderer(this);
    this.eventHandler = new EventHandler(this);
    this.dependencyManager = new DependencyManager(this);
    this.connectionManager = new ConnectionManager(this, this.dependencyManager);
    this.markerManager = new MarkerManager(this);
    this.workingDaysManager = new WorkingDaysManager(this);

    this._updateLanguageNames();
    this._initializeDOM();
    this.eventHandler.setupEventListeners();

    if (this.language) {
      this.language.onChange(() => this._handleLanguageChange());

      this.language.completeInitialization();
    }
  }

  /**
   * Initialize language support
   * @param {Object} options - Configuration options
   * @private
   */
  _initializeLanguage(options) {
    if (window.Language) {
      this.language = new Language({
        defaultLanguage: options.defaultLanguage || 'en',
        languageBasePath: options.languageBasePath || 'lang/'
      });

      this.translate = (key, params) => this.language.translate(key, params);
      this.t = (key, params) => this.language.translate(key, params);
    } else {
      console.warn('Language class not available. Internationalization is disabled.');

      this.translate = (key) => key;
      this.t = (key) => key;
    }
  }

  /**
   * Update month and day names from language system
   * @private
   */
  _updateLanguageNames() {
    this.monthNames = [
      this.t('January'),
      this.t('February'),
      this.t('March'),
      this.t('April'),
      this.t('May'),
      this.t('June'),
      this.t('July'),
      this.t('August'),
      this.t('September'),
      this.t('October'),
      this.t('November'),
      this.t('December')
    ];

    this.dayNames = [
      this.t('Sun'),
      this.t('Mon'),
      this.t('Tue'),
      this.t('Wed'),
      this.t('Thu'),
      this.t('Fri'),
      this.t('Sat')
    ];

    this.lang = {
      monthNames: this.monthNames,
      dayNames: this.dayNames,
      buttonLabels: {
        prevMonth: this.t('Previous month'),
        nextMonth: this.t('Next month'),
        addConnection: this.t('Add connection'),
        cancelConnection: this.t('Cancel connection'),
        addMarker: this.t('Add marker'),
        cancelMarker: this.t('Cancel marker'),
        clearAll: this.t('Clear all'),
        addGroup: this.t('Add group'),
        deleteConnection: this.t('Delete')
      },
      tooltips: {
        connectionMode: this.t('Click on a source block, then click on a destination block to create a connection'),
        markerTooltip: this.t('Day {day} {month} {year}')
      },
      prompts: {
        taskName: this.t('Enter task name:'),
        taskDisplayText: this.t('Enter text to display on the block:'),
        blockDisplayText: this.t('Enter text to display on the block:'),
        blockIcon: this.t('Enter HTML code for the icon (e.g. &#9733; or leave blank):')
      },
      labels: {
        connections: this.t('Connections'),
        noConnections: this.t('No connections'),
        selectColor: this.t('Select color:'),
        newGroupName: this.t('New group name...'),
        tasksCount: this.t('Number of tasks in group')
      }
    };
  }

  /**
   * Forces an update of the calendar's language settings and refreshes the UI.
   * This method updates month/day names, synchronizes with the data manager,
   * and triggers a complete re-render of the calendar.
   *
   * @returns {this} Returns the instance for method chaining
   */
  forceUpdateLanguage() {
    this._updateLanguageNames();
    if (this.dataManager) {
      this.dataManager._updateNamesFromTimeline();
    }
    this.renderCalendar({forceFull: true});
    return this;
  }

  /**
   * Handle language change
   * @private
   */
  _handleLanguageChange() {
    this._updateLanguageNames();

    const hasData = this.dataManager &&
      (this.dataManager.taskGroups.length > 0 ||
        this.dataManager.blocks.length > 0);

    if (hasData) {
      this.renderCalendar({forceFull: true});
    }

    if (this.markerManager) {
      this.markerManager.renderMarkers();
    }

    if (this.connectionManager) {
      this.connectionManager.updateConnections();
    }

    this.trigger('languageChanged', {language: this.getCurrentLanguage()});
  }

  /**
   * Initialize options with defaults
   * @param {Object} options - User provided options
   * @returns {Object} - Complete options with defaults
   * @private
   */
  _initializeOptions(options) {
    return Object.assign({
      container: 'timeline-app',
      defaultTaskText: '',
      connectionLineColor: '#000000',
      defaultIcon: null,
      headerWidth: 120,
      itemWidth: 30,
      lockMarkerToTopRow: false,
      syncConnectedBlocks: true,
      arrowSize: 5,
      lineThickness: 2,
      markerThickness: 2,
      preventOverlap: true,
      onBlockCreate: null,
      onBlockUpdate: null,
      onConnectionCreate: null,
      onMarkerCreate: null,
      onDataChange: null,
      onBlockDblClick: null,
      onMarkerDblClick: null,
      showHolidays: options.showHolidays !== undefined ? options.showHolidays : true,
      filterRange: options.filterRange || null,
      defaultBlockDuration: 4,
      allowRowCrossing: options.allowRowCrossing !== undefined ? options.allowRowCrossing : true,
      snapUnit: options.snapUnit || 1,
      enableSnapping: options.enableSnapping !== undefined ? options.enableSnapping : true,
      snapThreshold: options.snapThreshold || 0.3,
      snapFeedback: options.snapFeedback !== undefined ? options.snapFeedback : true,
      snapToWeekStart: options.snapToWeekStart !== undefined ? options.snapToWeekStart : false,
      snapStrength: options.snapStrength || 'medium',
      useServerSideConnections: options.useServerSideConnections !== undefined ? options.useServerSideConnections : false,
      allowClickToAdd: options.allowClickToAdd !== undefined ? options.allowClickToAdd : false,
      showAllConnections: options.showAllConnections !== undefined ? options.showAllConnections : false,
      weekEnds: [0, 6]
    }, options);
  }

  /**
   * Initialize DOM elements
   * @private
   */
  _initializeDOM() {
    this.container = document.getElementById(this.options.container);
    this.container.innerHTML = '';
    this.container.className = 'timeline-app';

    this.headerArea = document.createElement('div');
    this.headerArea.className = 'header-area';
    this.container.appendChild(this.headerArea);

    this.timelineContainer = document.createElement('div');
    this.timelineContainer.className = 'timeline-container';
    this.container.appendChild(this.timelineContainer);

    this.dayHeaders = document.createElement('div');
    this.dayHeaders.className = 'day-headers';
    this.timelineContainer.appendChild(this.dayHeaders);

    this.tasksArea = document.createElement('div');
    this.tasksArea.className = 'tasks-area';
    this.timelineContainer.appendChild(this.tasksArea);

    this.connectionSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    this.connectionSvg.setAttribute("class", "connection-svg");
    this.connectionSvg.style.position = "absolute";
    this.connectionSvg.style.top = "0";
    this.connectionSvg.style.left = "0";
    this.connectionSvg.style.pointerEvents = "none";
    this.connectionSvg.style.zIndex = "4";
    if (this.timelineContainer) {
      this.timelineContainer.appendChild(this.connectionSvg);
    }
  }

  /**
   * Set the application language
   * @param {string} languageCode - Language code to set
   * @returns {Promise} - Promise that resolves when language is set
   */
  async setLanguage(languageCode) {
    if (!this.language) {
      console.warn('Language system not initialized');
      return Promise.reject('Language system not initialized');
    }

    try {
      await this.language.setLanguage(languageCode);
      return languageCode;
    } catch (error) {
      this.showNotification(`Failed to set language: ${error.message}`, 'error');
      return Promise.reject(error);
    }
  }

  /**
   * Get the current language code
   * @returns {string} - Current language code
   */
  getCurrentLanguage() {
    return this.language ? this.language.getCurrentLanguage() : 'en';
  }

  /**
   * Register custom translations
   * @param {string} languageCode - Language code
   * @param {Object} translations - Key-value pairs of translations
   */
  registerTranslations(languageCode, translations) {
    if (this.language && languageCode && translations) {
      this.language.register(languageCode, translations);
    }
  }

  /**
   * Set a filter range for the timeline
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {TimelineCalendar} - The timeline instance for chaining
   */
  setFilterRange(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      this.showNotification(this.t('Invalid date format. Please use YYYY-MM-DD format'), 'error');
      return this;
    }

    if (start > end) {
      this.showNotification(this.t('Start date must be before end date'), 'error');
      return this;
    }

    // Set new filter range
    this.options.filterRange = {
      start: this.formatDate(start, 'y-m-d', 0),
      end: this.formatDate(end, 'y-m-d', 0)
    };

    if (this.currentProject && this.workingDaysManager) {
      // Reload working days with new filter range (similar to setWorkingDays)
      this.workingDaysManager.buildMaps();

      // Re-render everything
      this.renderCalendar({forceFull: true});
    }

    return this;
  }

  /**
   * Render the calendar with current data
   * @param {Object} options - Rendering options
   */
  renderCalendar(options = {}) {
    const emptyState = this._checkEmptyState();
    if (emptyState) {
      console.warn('TimelineCalendar.renderCalendar - Empty state detected:', options);
      this._renderEmptyState(emptyState);
      return;
    }

    const forceFull = options.forceFull || true;
    const skipHeaderCalculation = options.skipHeaderCalculation || false;
    const showHolidaysChanged = options.showHolidaysChanged || false;
    const settingsChanged = options.settingsChanged || false;
    const headerWidthChanged = options.headerWidthChanged || false;
    const itemWidthChanged = options.itemWidthChanged || false;
    const layoutChanged = options.layoutChanged || headerWidthChanged || itemWidthChanged;
    const blockBehaviorChanged = options.blockBehaviorChanged || false;

    requestAnimationFrame(() => {
      if (!skipHeaderCalculation && (forceFull || !this.headers.length || showHolidaysChanged)) {
        this.calculateHeaders();
      }

      if (forceFull || headerWidthChanged) {
        const headerWidth = `${this.options.headerWidth}px`;

        if (this.headerArea) {
          this.headerArea.style.width = headerWidth;
        }

        document.querySelectorAll('.task-header, .task-group-header').forEach(header => {
          header.style.width = headerWidth;
        });
      }

      this.domRenderer.renderDayHeaders();
      this.domRenderer.renderHeaderArea();
      this.domRenderer.renderTaskRows();

      if (forceFull || layoutChanged || showHolidaysChanged) {
        this.dataManager.blocks.forEach(block => {
          this.dataManager.updateBlockDates(block);
        });
      }

      this.domRenderer.renderBlocks();

      if (forceFull || blockBehaviorChanged || layoutChanged) {
        this.dependencyManager.rebuildDependencyMap();

        if (options.syncConnectedBlocksChanged) {
          this.connectionManager.connections.forEach(connection => {
            const fromBlock = this.dataManager.blocks.find(b => b.id === connection.fromId);
            const toBlock = this.dataManager.blocks.find(b => b.id === connection.toId);

            if (fromBlock && toBlock) {
              connection.gap = this.connectionManager.calculateGapBetweenBlocks(fromBlock, toBlock);
            }
          });
        }
      }

      this.markerManager.renderMarkers();
      this.connectionManager.updateConnections();
      this.connectionManager.setupArrowMarkers();
      this.processTaskQueue();

      if (settingsChanged) {
        this.trigger('afterSettingsRender', options);
      } else {
        this.trigger('afterRender');
      }
    });
  }

  /**
   * Add a task to the processing queue
   * @param {Function} task - Task function to execute
   * @param {number} priority - Priority (lower is higher priority)
   */
  addToTaskQueue(task, priority = 5) {
    this.taskQueue.push({task, priority});
    this.taskQueue.sort((a, b) => a.priority - b.priority);

    if (!this.isProcessingQueue) {
      this.processTaskQueue();
    }
  }

  /**
   * Process tasks in the task queue
   */
  processTaskQueue() {
    if (this.taskQueue.length === 0) {
      this.isProcessingQueue = false;
      return;
    }

    this.isProcessingQueue = true;
    const nextTask = this.taskQueue.shift();

    try {
      nextTask.task();
    } catch (error) {
      console.error('Error processing task queue:', error);
    }

    requestAnimationFrame(() => {
      this.processTaskQueue();
    });
  }

  /**
   * Add a new task group
   */
  addNewGroup() {
    const input = document.getElementById('new-group-name');
    const taskCount = document.getElementById('new-group-count');
    const groupName = input?.value.trim() || '';
    const count = parseInt(taskCount?.value) || 1;

    if (groupName) {
      this.dataManager.addTaskGroup(groupName, count);
      if (input) input.value = '';
      if (taskCount) taskCount.value = '1';
      this.renderCalendar();
      this.dataManager.saveDataToServer();
    }
  }

  /**
   * Clear all data from the timeline
   */
  clearAll() {
    this.dataManager.clearAllData();
    this.renderCalendar();
    this.dataManager.saveDataToServer();
    this.triggerDataChangeCallback();
  }

  /**
   * Process tree data to build header structure
   */
  processTreeData() {
    this.headerStructure = {
      mainGroups: [],
      subGroups: {}
    };

    if (!this.treeData || !Array.isArray(this.treeData)) {
      return;
    }

    const nodesByLevel = {};
    const treeLevels = this.treeData
      .map(node => parseInt(node.level))
      .filter(level => !Number.isNaN(level));
    const levelOffset = treeLevels.length > 0 && Math.min(...treeLevels) === 0 ? 1 : 0;

    for (const node of this.treeData) {
      const level = parseInt(node.level) + levelOffset;
      if (!nodesByLevel[level]) {
        nodesByLevel[level] = [];
      }
      nodesByLevel[level].push({
        ...node,
        id: parseInt(node.id),
        parent_id: node.parent_id !== null ? parseInt(node.parent_id) : null,
        level: level,
        node_order: parseInt(node.node_order),
        children: []
      });
    }

    for (const level in nodesByLevel) {
      nodesByLevel[level].sort((a, b) => a.node_order - b.node_order);
    }

    if (nodesByLevel[1]) {
      this.headerStructure.mainGroups = nodesByLevel[1];

      for (const mainGroup of this.headerStructure.mainGroups) {
        this.headerStructure.subGroups[mainGroup.id] = [];
      }
    }

    if (nodesByLevel[2]) {
      for (const subGroup of nodesByLevel[2]) {
        if (subGroup.parent_id && this.headerStructure.subGroups[subGroup.parent_id]) {
          const subGroupWithTasks = {
            ...subGroup,
            tasks: []
          };
          this.headerStructure.subGroups[subGroup.parent_id].push(subGroupWithTasks);
        }
      }
    }

    if (nodesByLevel[3]) {
      for (const task of nodesByLevel[3]) {
        if (task.parent_id) {
          for (const mainGroupId in this.headerStructure.subGroups) {
            const subGroups = this.headerStructure.subGroups[mainGroupId];

            for (let i = 0; i < subGroups.length; i++) {
              if (subGroups[i].id === task.parent_id) {
                subGroups[i].tasks.push(task);
                break;
              }
            }
          }
        }
      }
    }

    this.calculateRowIndices();
  }

  calculateRowIndices() {
    let totalRowIndex = 0;

    for (const mainGroup of this.headerStructure.mainGroups) {
      const subGroups = this.headerStructure.subGroups[mainGroup.id] || [];

      for (const subGroup of subGroups) {
        const tasks = subGroup.tasks || [];

        if (tasks.length === 0) {
          subGroup.rowSpan = 1;
          subGroup.startRowIndex = totalRowIndex;
          totalRowIndex += 1;
        } else {
          subGroup.rowSpan = tasks.length;
          subGroup.startRowIndex = totalRowIndex;

          for (let i = 0; i < tasks.length; i++) {
            tasks[i].rowIndex = totalRowIndex + i;
          }

          totalRowIndex += tasks.length;
        }
      }
    }

    this.totalRows = totalRowIndex;
  }

  /**
   * Load a project by ID
   * @param {number|string} projectId - Project ID to load
   */
  async loadProject(projectId) {
    await this.dataManager.loadProject(projectId);
    this.renderCalendar({forceFull: true});
  }

  /**
   * Calculate date headers for the timeline
   */
  calculateHeaders() {
    this.headers = this.workingDaysManager.buildHeaders(
      this.options.filterRange.start,
      this.options.filterRange.end
    );

    this.trigger('headersCalculated', this.headers);
    return this.headers;
  }

  /**
   * Format a date as string
   * @param {Date} date - Date to format
   * @param {string} format - Format string (d-m-y)
   * @param {number} yearOffset - Year offset
   * @returns {string} - Formatted date string
   */
  formatDate(date, format = 'd-m-y', yearOffset = 0) {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear() + yearOffset);
    return format.replace('d', day)
      .replace('m', month)
      .replace('y', year);
  }

  /**
   * Escape HTML special characters
   * @param {string} unsafe - Unsafe string
   * @returns {string} - Escaped string
   */
  escapeHtml(unsafe) {
    if (!unsafe) return '';
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /**
   * Trigger a callback defined in options
   * @param {string} callbackName - Name of the callback
   * @param {*} data - Data to pass to the callback
   */
  triggerCallback(callbackName, data) {
    if (typeof this.options[callbackName] === 'function') {
      this.options[callbackName](data);
    }
  }

  /**
   * Trigger the data change callback
   */
  triggerDataChangeCallback() {
    if (typeof this.options.onDataChange === 'function') {
      this.options.onDataChange({
        taskGroups: this.dataManager.taskGroups,
        blocks: this.dataManager.blocks,
        connections: this.dataManager.connections,
        markers: this.dataManager.markers
      });
    }
  }

  /**
   * Edit a block
   * @param {Object} block - Block to edit
   */
  editBlock(block) {
    if (this.currentMode === 'normal') {
      this.triggerCallback('onBlockDblClick', block);
    }
  }

  /**
   * Edit a marker
   * @param {Object} marker - Marker to edit
   */
  editMarker(marker) {
    if (this.currentMode === 'normal') {
      this.triggerCallback('onMarkerDblClick', marker);
    }
  }

  /**
   * Get all data from the timeline
   * @returns {Object} - All timeline data
   */
  getAllData() {
    return this.dataManager.getAllData();
  }

  /**
   * Show notification using UI callback or fallback method
   * @param {string} message - Message to show
   * @param {string} type - Notification type (success, error, warning, info)
   * @param {number} duration - Duration in milliseconds
   */
  showNotification(message, type = 'success', duration = 3000) {
    if (this.uiCallbacks.onShowNotification) {
      this.uiCallbacks.onShowNotification(message, type, duration);
      return;
    }

    this._defaultShowNotification(message, type, duration);
  }

  /**
   * Register an event handler
   * @param {string} eventName - Event name
   * @param {Function} callback - Event handler function
   * @returns {TimelineCalendar} - The timeline instance for chaining
   */
  on(eventName, callback) {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }
    this.events[eventName].push(callback);
    return this;
  }

  /**
   * Remove an event handler
   * @param {string} eventName - Event name
   * @param {Function} callback - Event handler function to remove
   * @returns {TimelineCalendar} - The timeline instance for chaining
   */
  off(eventName, callback) {
    if (!this.events[eventName]) return this;

    if (callback) {
      this.events[eventName] = this.events[eventName].filter(cb => cb !== callback);
    } else {
      delete this.events[eventName];
    }

    return this;
  }

  /**
   * Trigger an event
   * @param {string} eventName - Event name
   * @param {*} data - Data to pass to event handlers
   * @returns {TimelineCalendar} - The timeline instance for chaining
   */
  trigger(eventName, data) {
    if (!this.events[eventName]) return;

    this.events[eventName].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event ${eventName}:`, error);
      }
    });

    return this;
  }

  /**
   * Update a block with new data
   * @param {Object} block - Block to update
   * @param {Object} newData - New block data
   * @returns {boolean} - True if update was successful
   */
  updateBlockFromEdit(block, newData) {
    if (!block || !newData) return false;

    const originalData = {
      displayText: block.displayText,
      icon: block.icon,
      color: block.color,
      progress: block.progress,
      startDate: block.startDate,
      duration: block.duration,
      startDay: block.startDay
    };

    if (!this.validateBlockData(block, newData)) {
      return false;
    }

    // Create a blocksChanged object to track what's changed
    const blocksChanged = {
      id: block.id,
      tempId: block.id || block.tempId,
      row_index: block.rowIndex,
      start_date: newData.startDate,
      duration: parseInt(newData.duration) || 1,
      color: newData.color,
      display_text: newData.displayText,
      icon: newData.icon,
      progress: parseInt(newData.progress) || 0
    };

    if (this.options.useServerSideConnections && !this.options.syncConnectedBlocks) {
      this.sendBlockUpdateToAPI(blocksChanged);
    }

    block.displayText = newData.displayText;
    block.icon = newData.icon;
    block.color = newData.color;
    block.progress = parseInt(newData.progress) || 0;

    let dateChanged = false;

    if (newData.startDate !== block.startDate || newData.duration !== block.duration) {
      block.startDate = newData.startDate;
      block.duration = parseInt(newData.duration) || 1;

      block.startDay = this.workingDaysManager.calculateBLockIndex(block.startDate);
      block.left = this.workingDaysManager.dateToPosition(block.startDate, this.options.itemWidth);

      this.dataManager.updateBlockDates(block);
      dateChanged = true;

      if (this.options.syncConnectedBlocks) {
        const dayChange = block.startDay - originalData.startDay;
        if (dayChange !== 0) {
          this.connectionManager.syncConnectedBlocks(block, dayChange);
        }
      }
    }

    const blockElement = document.querySelector(`.task-block[data-id="${block.id}"]`);
    if (blockElement) {
      this.domRenderer.renderBlock(block, blockElement);

      if (dateChanged) {
        this.connectionManager.updateConnections();
      }
    }

    this.sendBlockUpdateToAPI(blocksChanged);

    this.triggerCallback('onBlockUpdate', block);
    this.triggerDataChangeCallback();

    return true;
  }

  /**
   * Update a block with new data from server response
   * @param {string|number} blockId - Block ID to update
   * @param {Object} blockData - Updated block data
   * @returns {boolean} - True if update was successful
   */
  updateBlock(blockId, blockData) {
    if (!blockId || !blockData) {
      console.warn('updateBlock: Invalid parameters', {blockId, blockData});
      return false;
    }

    // Find the block in the dataManager
    const block = this.dataManager.blocks.find(b => b.id == blockId);
    if (!block) {
      console.warn('updateBlock: Block not found', blockId);
      return false;
    }

    console.log('Updating block:', blockId, blockData);

    // Store original data for comparison
    const originalData = {
      displayText: block.displayText,
      icon: block.icon,
      color: block.color,
      progress: block.progress,
      startDate: block.startDate,
      duration: block.duration,
      startDay: block.startDay
    };

    // Update block properties
    if (blockData.display_text !== undefined) {
      block.displayText = blockData.display_text;
    }
    if (blockData.color !== undefined) {
      block.color = blockData.color;
    }
    if (blockData.icon !== undefined) {
      block.icon = blockData.icon;
    }
    if (blockData.progress !== undefined) {
      block.progress = parseInt(blockData.progress) || 0;
    }

    let dateChanged = false;
    let positionChanged = false;

    // Update dates and position if changed
    if (blockData.start_date && blockData.start_date !== block.startDate) {
      block.startDate = blockData.start_date;
      block.startDay = this.workingDaysManager.calculateBLockIndex(block.startDate);
      block.left = this.workingDaysManager.dateToPosition(block.startDate, this.options.itemWidth);
      dateChanged = true;
      positionChanged = true;
    }

    if (blockData.duration && parseInt(blockData.duration) !== block.duration) {
      block.duration = parseInt(blockData.duration) || 1;
      dateChanged = true;
    }

    if (blockData.row_index !== undefined && blockData.row_index !== block.rowIndex) {
      block.rowIndex = blockData.row_index;
      positionChanged = true;
    }

    // Update block dates in data manager
    if (dateChanged) {
      this.dataManager.updateBlockDates(block);
    }

    // Find and update the block element in DOM
    const blockElement = document.querySelector(`.task-block[data-id="${block.id}"]`);
    if (blockElement) {
      // Re-render the block with new data
      this.domRenderer.renderBlock(block, blockElement);

      // Update position if needed
      if (positionChanged) {
        this.domRenderer.updateBlockPosition(blockElement, block);
      }

      // Update connections if dates changed
      if (dateChanged && this.connectionManager) {
        this.connectionManager.updateConnections();
      }
    }

    // Sync connected blocks if enabled and dates changed
    if (dateChanged && this.options.syncConnectedBlocks) {
      const dayChange = block.startDay - originalData.startDay;
      if (dayChange !== 0) {
        this.connectionManager.syncConnectedBlocks(block, dayChange);
      }
    }

    // Trigger callbacks
    this.triggerCallback('onBlockUpdate', block);
    this.triggerDataChangeCallback();

    console.log('Block updated successfully:', block);
    return true;
  }

  /**
   * Update a marker with new data from server response
   * @param {string|number} markerId - Marker ID to update
   * @param {Object} markerData - Updated marker data
   * @returns {boolean} - True if update was successful
   */
  updateMarker(markerId, markerData) {
    if (!markerId || !markerData) {
      console.warn('updateMarker: Invalid parameters', {markerId, markerData});
      return false;
    }

    // Find the marker in the dataManager
    const marker = this.dataManager.markers.find(m => m.id == markerId || m.tempId == markerId);
    if (!marker) {
      console.warn('updateMarker: Marker not found', markerId);
      return false;
    }

    console.log('Updating marker:', markerId, markerData);

    // Store original data for comparison
    const originalData = {
      name: marker.name,
      description: marker.description,
      color: marker.color,
      day: marker.day,
      rowIndex: marker.rowIndex
    };

    // Update marker properties
    if (markerData.name !== undefined) {
      marker.name = markerData.name;
    }
    if (markerData.description !== undefined) {
      marker.description = markerData.description;
    }
    if (markerData.color !== undefined) {
      marker.color = markerData.color;
    }
    if (markerData.icon !== undefined) {
      marker.icon = markerData.icon;
    }

    let positionChanged = false;

    // Update position if day or row changed
    if (markerData.day !== undefined && markerData.day !== marker.day) {
      marker.day = markerData.day;
      positionChanged = true;
    }

    if (markerData.rowIndex !== undefined && markerData.rowIndex !== marker.rowIndex) {
      marker.rowIndex = markerData.rowIndex;
      positionChanged = true;
    }

    // Find and update the marker element in DOM
    const markerElement = document.querySelector(`.timeline-marker[data-id="${marker.id || marker.tempId}"]`);
    if (markerElement) {
      // Re-render the marker with new data
      this.markerManager.renderMarker(marker);
    } else {
      // If marker element doesn't exist, render it
      this.markerManager.renderMarker(marker);
    }

    // Trigger callbacks
    this.triggerCallback('onMarkerUpdate', marker);
    this.triggerDataChangeCallback();

    console.log('Marker updated successfully:', marker);
    return true;
  }

  /**
   * Sends a block update to the API with enhanced server-side connection handling
   * @param {Object} blockData - Block data to update
   * @param {boolean} markAsChanged - Whether to mark the block as changed
   * @param {Object} options - Additional options for the update
   * @returns {Promise<Object>} - API response
   */
  async sendBlockUpdateToAPI(blockData, markAsChanged = true, options = {}) {
    const blockId = blockData.id || blockData.tempId;

    if (!this._canUpdateBlock(blockId)) {
      return {success: false, error: 'Too frequent updates'};
    }

    this._recordBlockUpdate(blockId, true);

    try {
      if (markAsChanged) {
        this.dataManager.changedBlocks.delete(blockId);
      }

      const settings = {
        showHolidays: this.options.showHolidays,
        preventOverlap: this.options.preventOverlap,
        syncConnectedBlocks: this.options.syncConnectedBlocks,
        useServerSideConnections: this.options.useServerSideConnections,
        allowRowCrossing: this.options.allowRowCrossing,

        projectId: this.currentProject?.id,
        calculateConnectedBlocks: this.options.useServerSideConnections && this.options.syncConnectedBlocks,

        isDragMode: options.isDragMode || false,
        isResizeMode: options.isResizeMode || false
      };

      const result = await window.apiHandler.saveChangedBlocks(
        [blockData],
        this.currentProject.id,
        settings
      ).catch(error => {
        console.error('An error in sending data to API:', error);

        if (this.options.useServerSideConnections && this.options.syncConnectedBlocks) {
          this._handleConnectionUpdateError(blockData, error);
        }

        return {success: false, error: error.message};
      });

      this._recordBlockUpdate(blockId, false);

      if (result.success) {
        if (this.options.useServerSideConnections) {
          if (result.data && result.data.updatedBlocks && result.data.updatedBlocks.length > 0) {
            this.updateBlocksFromServer(result.data.updatedBlocks);
          }

          if (result.data && result.data.updatedConnections && result.data.updatedConnections.length > 0) {
            this.updateConnectionsFromServer(result.data.updatedConnections);
          } else if (this.options.syncConnectedBlocks) {
            this.connectionManager.updateConnections();
          }
        }

        return result;
      } else {
        console.error('An error in the block update:', result.error);

        if (markAsChanged) {
          this.dataManager.changedBlocks.add(blockId);
        }

        if (this.options.useServerSideConnections && this.options.syncConnectedBlocks) {
          this._handleConnectionUpdateError(blockData, result.error || 'Unknown error');
        }

        return result;
      }
    } catch (error) {
      console.error('An error in sending data to API:', error);
      this._recordBlockUpdate(blockId, false);

      if (markAsChanged) {
        this.dataManager.changedBlocks.add(blockId);
      }

      if (this.options.useServerSideConnections && this.options.syncConnectedBlocks) {
        this._handleConnectionUpdateError(blockData, error);
      }

      return {success: false, error: error.message};
    }
  }

  handleBlockDragEnd(block, newPosition) {
    const blockData = {
      id: block.id,
      tempId: block.tempId,
      row_index: newPosition.rowIndex,
      start_date: newPosition.startDate,
      duration: block.duration,
      color: block.color,
      display_text: block.displayText,
      icon: block.icon,
      progress: block.progress
    };

    this.sendBlockUpdateToAPI(blockData, true, {
      isDragMode: true,
      isResizeMode: false
    });
  }

  handleBlockResizeEnd(block, newSize) {
    const blockData = {
      id: block.id,
      tempId: block.tempId,
      row_index: block.rowIndex,
      start_date: block.startDate,
      duration: newSize.duration,
      color: block.color,
      display_text: block.displayText,
      icon: block.icon,
      progress: block.progress
    };

    this.sendBlockUpdateToAPI(blockData, true, {
      isDragMode: false,
      isResizeMode: true
    });
  }

  /**
   * ส่งข้อมูลบล็อกหลายรายการไปยัง API ทันที
   * @async
   * @param {Array} blocksData - รายการข้อมูลบล็อกที่ต้องการส่ง
   * @param {boolean} [markAsChanged=true] - ระบุว่าต้องการทำเครื่องหมายบล็อกเหล่านี้ว่าได้ถูกบันทึกแล้วหรือไม่
   * @param {number} [delayMs=0] - ระยะเวลาหน่วงเป็นมิลลิวินาทีก่อนส่งข้อมูล (ใช้สำหรับลดการส่งข้อมูลถี่เกินไป)
   * @returns {Promise<Object>} ข้อมูลผลลัพธ์จากการส่ง
   */
  async sendBlocksUpdateToAPI(blocksData, markAsChanged = true, delayMs = 0) {
    if (!blocksData || blocksData.length === 0) {
      return {success: false, message: 'No blog information to send'};
    }

    if (delayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    try {
      if (window.apiHandler) {
        if (markAsChanged) {
          blocksData.forEach(block => {
            const blockId = block.id || block.tempId;
            if (blockId) {
              this.dataManager.changedBlocks.delete(blockId);
            }
          });
        }

        const result = await window.apiHandler.saveChangedBlocks(blocksData, this.currentProject.id);

        if (result.success) {
          if (result.data && result.data.updatedConnections) {
            this.updateConnectionsFromServer(result.data.updatedConnections);
          }

          return {
            success: true,
            message: 'All blogs successfully record',
            data: result.data
          };
        } else {
          console.error('There was an error in the block group update.:', result.error);

          if (markAsChanged) {
            blocksData.forEach(block => {
              const blockId = block.id || block.tempId;
              if (blockId) {
                this.dataManager.changedBlocks.add(blockId);
              }
            });
          }

          return {
            success: false,
            error: result.error,
            message: 'An error from the server'
          };
        }
      } else {
        console.error('No APIHANDLER was found to send data to API.');
        return {
          success: false,
          message: 'No API manager'
        };
      }
    } catch (error) {
      console.error('An error in sending data to API:', error);

      if (markAsChanged) {
        blocksData.forEach(block => {
          const blockId = block.id || block.tempId;
          if (blockId) {
            this.dataManager.changedBlocks.add(blockId);
          }
        });
      }

      if (!this._pendingUpdates) this._pendingUpdates = [];

      blocksData.forEach(block => {
        const blockId = block.id || block.tempId;
        const existingIndex = this._pendingUpdates.findIndex(item =>
          (item.id && item.id === blockId) || (item.tempId && item.tempId === blockId)
        );

        if (existingIndex >= 0) {
          this._pendingUpdates[existingIndex] = block;
        } else {
          this._pendingUpdates.push(block);
        }
      });

      this.showNotification(
        this.t('Unable to send data to the server Will try again when there is a connection'),
        'warning'
      );

      return {
        success: false,
        error: error.message,
        message: 'An error in data transmission'
      };
    }
  }

  /**
   * ลองส่งข้อมูลที่รอการส่งทั้งหมด
   * @returns {Promise<boolean>} true ถ้าการส่งทั้งหมดสำเร็จ
   */
  async retryPendingUpdates() {
    if (!this._pendingUpdates || this._pendingUpdates.length === 0) {
      return true;
    }

    try {
      const pendingBlocks = [...this._pendingUpdates];
      this._pendingUpdates = [];

      const result = await this.sendBlocksUpdateToAPI(pendingBlocks, true);

      if (result.success) {
        this.showNotification(
          this.t('Send information waiting for success.'),
          'success'
        );
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('There was an error while trying to send information waiting for delivery.:', error);
      return false;
    }
  }

  /**
   * Retry failed connection updates
   * This method attempts to recover from connection update failures
   * @param {number} maxRetries - Maximum number of retry attempts
   * @returns {Promise<boolean>} - True if all retries were successful
   */
  async retryFailedConnectionUpdates(maxRetries = 3) {
    if (!this._connectionUpdateErrors || this._connectionUpdateErrors.length === 0) {
      return true;
    }

    const errorsToRetry = [...this._connectionUpdateErrors];
    this._connectionUpdateErrors = [];

    const blockGroups = {};
    errorsToRetry.forEach(error => {
      if (!blockGroups[error.blockId]) {
        blockGroups[error.blockId] = [];
      }
      blockGroups[error.blockId].push(error);
    });

    let success = true;

    for (const blockId in blockGroups) {
      const block = this.dataManager.blocks.find(b =>
        (b.id && b.id.toString() === blockId) ||
        (b.tempId && b.tempId.toString() === blockId)
      );

      if (!block) {
        continue;
      }

      const blockData = {
        id: block.id,
        tempId: block.tempId,
        row_index: block.rowIndex,
        start_date: block.startDate,
        duration: block.duration,
        color: block.color,
        display_text: block.displayText,
        icon: block.icon,
        progress: block.progress
      };

      let retryCount = 0;
      let retrySuccess = false;

      while (retryCount < maxRetries && !retrySuccess) {
        try {
          if (retryCount > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          const result = await this.sendBlockUpdateToAPI(blockData, false, {
            retryAttempt: retryCount + 1
          });

          if (result.success) {
            retrySuccess = true;
          } else {
            console.error(`Retry failed for block ${blockId}:`, result.error);
            retryCount++;
          }
        } catch (error) {
          console.error(`Error during retry for block ${blockId}:`, error);
          retryCount++;
        }
      }

      if (!retrySuccess) {
        success = false;

        this._connectionUpdateErrors.push({
          blockId,
          timestamp: Date.now(),
          error: `Failed after ${maxRetries} retry attempts`
        });
      }
    }

    if (success) {
      this.showNotification(
        this.t('Updated a successful link.'),
        'success',
        3000
      );
    } else if (this._connectionUpdateErrors.length > 0) {
      this.showNotification(
        this.t('Unable to update some links Please try to refresh the webpage.'),
        'warning',
        5000
      );
    }

    return success;
  }

  /**
   * Update connections from server data
   * @param {Array} updatedConnections - Updated connection data from server
   * @private
   */
  updateConnectionsFromServer(updatedConnections) {
    if (!updatedConnections || !Array.isArray(updatedConnections) || updatedConnections.length === 0) {
      return;
    }

    let hasChanges = false;
    let updatedConnectionsCount = 0;

    updatedConnections.forEach(serverConn => {
      const localConn = this.connectionManager.getConnection(serverConn.id);

      if (localConn) {
        if (localConn.gap !== serverConn.gap) {
          localConn.gap = serverConn.gap;
          hasChanges = true;
          updatedConnectionsCount++;
        }

        if (serverConn.fromId !== undefined && localConn.fromId !== serverConn.fromId) {
          localConn.fromId = serverConn.fromId;
          hasChanges = true;
          updatedConnectionsCount++;
        }

        if (serverConn.toId !== undefined && localConn.toId !== serverConn.toId) {
          localConn.toId = serverConn.toId;
          hasChanges = true;
          updatedConnectionsCount++;
        }

        if (serverConn.type !== undefined && localConn.type !== serverConn.type) {
          localConn.type = serverConn.type;
          hasChanges = true;
          updatedConnectionsCount++;
        }
      } else if (serverConn.fromId && serverConn.toId) {
        this.dependencyManager.addConnection(
          serverConn.fromId,
          serverConn.toId,
          serverConn.id,
          serverConn.gap,
          serverConn.type || 'default'
        );
        hasChanges = true;
        updatedConnectionsCount++;
      }
    });

    if (hasChanges) {
      this.connectionManager.updateConnections();

      if (this.options.useServerSideConnections && this.options.syncConnectedBlocks) {
        if (updatedConnectionsCount > 1) {
          this.showNotification(
            this.t('Update all links {Count}', {count: updatedConnectionsCount}),
            'success',
            2500
          );
        } else {
          this.showNotification(this.t('Update the link successfully.'), 'success', 2000);
        }
      }

      this.trigger('connectionsUpdated', {
        count: updatedConnectionsCount,
        connections: updatedConnections
      });
    }
  }

  /**
   * Update local blocks with data received from server after drag/resize operations
   * @param {Array} updatedBlocks - Blocks data from server
   */
  updateBlocksFromServer(updatedBlocks) {
    if (!updatedBlocks || !Array.isArray(updatedBlocks) || updatedBlocks.length === 0) {
      return;
    }

    let hasPositionChanges = false;
    let hasVisualChanges = false;
    let hasNewBlocks = false;
    let updatedBlocksCount = 0;
    let dragResizeBlocks = [];

    const processedBlockIds = new Map();

    updatedBlocks.forEach(serverBlock => {
      const blockId = serverBlock.id || serverBlock.tempId;
      let localBlock;

      if (serverBlock.tempId) {
        localBlock = this.dataManager.blocks.find(b =>
          (b.tempId && b.tempId === serverBlock.tempId) ||
          (b.id === 0 && b.tempId === serverBlock.tempId)
        );
      }

      if (!localBlock && blockId) {
        localBlock = this.dataManager.blocks.find(b => b.id === blockId);
      }

      if (!localBlock && serverBlock.start_date && serverBlock.row_index !== undefined) {
        localBlock = {
          id: serverBlock.id,
          tempId: serverBlock.tempId,
          rowIndex: serverBlock.row_index,
          startDate: serverBlock.start_date,
          duration: serverBlock.duration || 1,
          color: serverBlock.color || '#4a6fff',
          displayText: serverBlock.display_text || '',
          icon: serverBlock.icon || null,
          progress: serverBlock.progress || 0
        };

        this.dataManager.blocks.push(localBlock);
        this.dataManager.updateBlockDates(localBlock);
        hasNewBlocks = true;
        updatedBlocksCount++;

        processedBlockIds.set(blockId, true);
      } else if (localBlock) {
        if (processedBlockIds.has(blockId)) {
          return;
        }

        processedBlockIds.set(blockId, true);

        const originalData = {
          id: localBlock.id,
          tempId: localBlock.tempId,
          rowIndex: localBlock.rowIndex,
          startDate: localBlock.startDate,
          duration: localBlock.duration,
          color: localBlock.color,
          displayText: localBlock.displayText,
          icon: localBlock.icon,
          progress: localBlock.progress
        };

        let localHasChanges = false;
        let localHasPositionChanges = false;
        let localHasVisualChanges = false;

        if (localBlock.id === 0 && serverBlock.id) {
          localBlock.id = serverBlock.id;

          localHasChanges = true;

          const blockElement = document.querySelector(`.task-block[data-id="${localBlock.tempId}"]`);
          if (blockElement) {
            blockElement.dataset.id = localBlock.id;
          }
        }

        if (serverBlock.row_index !== undefined && serverBlock.row_index !== localBlock.rowIndex) {
          localBlock.rowIndex = serverBlock.row_index;
          localHasChanges = true;
          localHasPositionChanges = true;
        }

        if (serverBlock.start_date && serverBlock.start_date !== localBlock.startDate) {
          localBlock.startDate = serverBlock.start_date;
          localHasChanges = true;
          localHasPositionChanges = true;
        }

        if (serverBlock.duration && serverBlock.duration !== localBlock.duration) {
          localBlock.duration = serverBlock.duration;
          localHasChanges = true;
          localHasPositionChanges = true;
        }

        if (serverBlock.color && serverBlock.color !== localBlock.color) {
          localBlock.color = serverBlock.color;
          localHasChanges = true;
          localHasVisualChanges = true;
        }

        if (serverBlock.display_text !== undefined && serverBlock.display_text !== localBlock.displayText) {
          localBlock.displayText = serverBlock.display_text;
          localHasChanges = true;
          localHasVisualChanges = true;
        }

        if (serverBlock.icon !== undefined && serverBlock.icon !== localBlock.icon) {
          localBlock.icon = serverBlock.icon;
          localHasChanges = true;
          localHasVisualChanges = true;
        }

        if (serverBlock.progress !== undefined && serverBlock.progress !== localBlock.progress) {
          localBlock.progress = serverBlock.progress;
          localHasChanges = true;
          localHasVisualChanges = true;
        }

        if (localHasPositionChanges) {
          this.dataManager.updateBlockDates(localBlock);
          hasPositionChanges = true;

          dragResizeBlocks.push({
            id: blockId,
            from: {
              startDate: originalData.startDate,
              duration: originalData.duration,
              rowIndex: originalData.rowIndex
            },
            to: {
              startDate: localBlock.startDate,
              duration: localBlock.duration,
              rowIndex: localBlock.rowIndex
            }
          });
        }

        if (localHasVisualChanges) {
          hasVisualChanges = true;
        }

        if (localHasChanges) {
          updatedBlocksCount++;
          const blockId = localBlock.id || localBlock.tempId;
          const blockElement = document.querySelector(`.task-block[data-id="${blockId}"]`);
          if (blockElement) {
            if (localHasVisualChanges && !localHasPositionChanges) {
              if (originalData.color !== localBlock.color) {
                blockElement.style.backgroundColor = localBlock.color;
              }

              if (originalData.progress !== localBlock.progress) {
                const progressBar = blockElement.querySelector('.task-progress');
                if (progressBar) {
                  progressBar.style.width = `${localBlock.progress}%`;
                }
              }
              const textElement = blockElement.querySelector('.task-text');
              if (textElement) {
                if (originalData.displayText !== localBlock.displayText) {
                  textElement.textContent = this.escapeHtml(localBlock.displayText);
                }

                if (originalData.icon !== localBlock.icon) {
                  textElement.classList.remove(originalData.icon);
                  textElement.classList.add(localBlock.icon);
                }
              }
            } else if (localHasPositionChanges) {
              this.domRenderer.renderBlock(localBlock, blockElement);
            }
          }
        }
      }
    });

    if (hasPositionChanges || hasNewBlocks) {
      this.connectionManager.updateConnections();

      if (dragResizeBlocks.length > 0) {
        this.trigger('blocksPositionChanged', {
          blocks: dragResizeBlocks,
          isDragMode: true
        });
      }
    }

    if (hasNewBlocks) {
      this.domRenderer.renderBlocks();
    }

    if (updatedBlocksCount > 0 && this.options.useServerSideConnections && this.options.syncConnectedBlocks) {
      if (updatedBlocksCount > 1) {
        this.showNotification(
          this.t('อัปเดตบล็อกทั้งหมด {count} รายการเรียบร้อย', {count: updatedBlocksCount}),
          'success',
          3000
        );
      } else if (hasPositionChanges) {
        this.showNotification(
          this.t('อัปเดตตำแหน่งบล็อกเรียบร้อย'),
          'success',
          2000
        );
      } else if (hasVisualChanges) {
        this.showNotification(
          this.t('อัปเดตรูปแบบบล็อกเรียบร้อย'),
          'success',
          2000
        );
      }
    }

    return updatedBlocksCount > 0;
  }

  /**
   * Validate block data
   * @param {Object} block - Block to validate
   * @param {Object} newData - New block data
   * @returns {boolean} - True if data is valid
   */
  validateBlockData(block, newData) {
    if (!newData.startDate) {
      this.showNotification(this.t('The beginning date must be in the specified calendar'), 'error');
      return false;
    }

    const duration = parseInt(newData.duration) || 0;
    if (duration < 1) {
      this.showNotification(this.t('The duration must be greater or equal to 1 day'), 'error');
      return false;
    }

    let isDateInRange = false;
    if (this.headers.includes(newData.startDate)) {
      isDateInRange = true;
    } else {
      const startDate = new Date(newData.startDate);
      const firstDate = new Date(this.headers[0]);
      const lastDate = new Date(this.headers[this.headers.length - 1]);

      if (startDate >= firstDate && startDate <= lastDate) {
        isDateInRange = true;
      }
    }

    if (!isDateInRange) {
      this.showNotification(this.t('The beginning date must be in the specified calendar'), 'error');
      return false;
    }

    if (this.options.preventOverlap) {
      const tempBlock = {...block};
      tempBlock.startDate = newData.startDate;
      tempBlock.duration = duration;

      const startDateObj = new Date(newData.startDate);
      const endDateObj = new Date(startDateObj);
      endDateObj.setDate(startDateObj.getDate() + duration - 1);
      tempBlock.endDate = this.formatDate(endDateObj, 'y-m-d', 0);

      if (this.dataManager.checkBlockDateOverlap(tempBlock)) {
        this.showNotification(this.t('Unable to record: Block will overlap with other blocks'), 'error');
        return false;
      }
    }

    return true;
  }

  /**
   * Save timeline settings to server and reload data
   * @param {Object} newSettings - New settings to save
   * @returns {Promise<boolean>} - True if successful
   */
  async saveAndReloadTimeline(newSettings) {
    try {
      const settings = {
        ...this.options,
        ...newSettings,
        projectId: this.currentProject.id,
        startDate: this.formatDate(this.options.filterRange.start, 'y-m-d', 0),
        endDate: this.formatDate(this.options.filterRange.end, 'y-m-d', 0)
      };

      // ผ่าน DataManager.requestApi() เพื่อให้ได้ X-CSRF-Token ติดไปด้วย
      const data = await this.dataManager.requestApi('save_timeline_settings', 'POST', settings);

      if (data && data.status === 'success') {
        this.dataManager.setProjectData(data.data);

        Object.assign(this.options, newSettings);

        if (typeof switchToTimelineTab === 'function') {
          switchToTimelineTab(() => {
            this.renderCalendar({
              forceFull: true,
              showHolidaysChanged: newSettings.showHolidays !== undefined,
              headerWidthChanged: newSettings.headerWidth !== undefined,
              itemWidthChanged: newSettings.itemWidth !== undefined,
              settingsChanged: true
            });
          });
        } else {
          this.renderCalendar({
            forceFull: true,
            showHolidaysChanged: newSettings.showHolidays !== undefined,
            headerWidthChanged: newSettings.headerWidth !== undefined,
            itemWidthChanged: newSettings.itemWidth !== undefined,
            settingsChanged: true
          });
        }

        this.connectionManager.updateConnections();

        this.markerManager.renderMarkers();

        const significantChange =
          newSettings.showHolidays !== undefined ||
          newSettings.headerWidth !== undefined ||
          newSettings.itemWidth !== undefined;

        if (significantChange) {
          this.dataManager.blocks.forEach(block => {
            this.dataManager.updateBlockDates(block);
          });
          this.domRenderer.renderBlocks();
        }

        this.showNotification(this.t('Settings saved successfully'), 'success');

        return true;
      } else {
        console.error("API returned error:", data);
        this.showNotification(this.t('Error saving settings: {message}', {message: data.message || 'Unknown error'}), 'error');
        throw new Error(data.message || 'Unknown error');
      }
    } catch (error) {
      console.error("Error in saveAndReloadTimeline:", error);
      this.showNotification(this.t('Error saving settings: {message}', {message: error.message}), 'error');
      return false;
    }
  }

  /**
   * Toggle connection visibility
   * @returns {boolean} - New visibility state
   */
  toggleConnectionVisibility() {
    const newState = this.connectionManager.toggleConnectionVisibility();
    this.showNotification(
      newState
        ? this.t('All connections visible')
        : this.t('Connections hidden (click on blocks to show their connections)'),
      'info'
    );
    return newState;
  }

  /**
   * Save only changed blocks to server
   * This allows more efficient saving when only a few blocks have changed
   * @returns {Promise<Object>} - Result of the save operation
   */
  async saveChangedBlocks() {
    return await this.dataManager.saveChangedBlocksToServer();
  }

  /**
   * ส่งข้อมูลการเชื่อมโยงไปยัง API โดยตรง
   * @param {Object} connectionData - ข้อมูลการเชื่อมโยงที่ต้องการอัปเดท
   * @returns {Promise<Object>} - ผลลัพธ์จาก API
   */
  async sendConnectionUpdateToAPI(connectionData) {
    if (!this.currentProject || !window.apiHandler) {
      return {success: false, message: 'No active project or API handler not available'};
    }

    const data = {
      projectId: this.currentProject.id,
      connection: connectionData
    };

    try {
      return await window.apiHandler.sendRequest('update_connection', 'POST', data);
    } catch (error) {
      console.error('Error sending connection update to API:', error);
      return {success: false, error: error.message};
    }
  }

  /**
   * ส่งข้อมูล marker ไปยัง API โดยตรง
   * @param {Object} markerData - ข้อมูล marker ที่ต้องการอัปเดท
   * @returns {Promise<Object>} - ผลลัพธ์จาก API
   */
  async sendMarkerUpdateToAPI(markerData) {
    if (!this.currentProject || !window.apiHandler) {
      return {success: false, message: 'No active project or API handler not available'};
    }

    const data = {
      projectId: this.currentProject.id,
      marker: markerData
    };

    try {
      return await window.apiHandler.sendRequest('update_marker', 'POST', data);
    } catch (error) {
      console.error('Error sending marker update to API:', error);
      return {success: false, error: error.message};
    }
  }

  /**
   * Toggle or set snapping behavior
   * @param {boolean} [enable] - Set to true/false to enable/disable, or leave undefined to toggle
   * @returns {boolean} - Current snapping state
   */
  toggleSnapping(enable) {
    if (enable !== undefined) {
      this.options.enableSnapping = Boolean(enable);
    } else {
      this.options.enableSnapping = !this.options.enableSnapping;
    }

    const settings = TimelineHelpers.loadFromStorage('timeline_settings', {});
    settings.enableSnapping = this.options.enableSnapping;
    TimelineHelpers.saveToStorage('timeline_settings', settings);

    const message = this.options.enableSnapping ?
      this.t('Block snapping enabled') :
      this.t('Block snapping disabled');
    this.showNotification(message, 'info');

    return this.options.enableSnapping;
  }

  /**
   * Set snapping parameters
   * @param {Object} params - Snapping parameters
   * @param {number} [params.snapUnit] - Snap unit in days
   * @param {string} [params.snapStrength] - Snap strength (weak, medium, strong)
   * @param {boolean} [params.snapFeedback] - Whether to show visual feedback
   * @param {boolean} [params.snapToWeekStart] - Whether to snap to the start of weeks
   * @returns {Object} - Current snapping settings
   */
  setSnappingParams(params = {}) {
    if (params.snapUnit !== undefined) {
      this.options.snapUnit = Number(params.snapUnit);
    }

    if (params.snapStrength !== undefined) {
      this.options.snapStrength = params.snapStrength;
    }

    if (params.snapFeedback !== undefined) {
      this.options.snapFeedback = Boolean(params.snapFeedback);
    }

    if (params.snapToWeekStart !== undefined) {
      this.options.snapToWeekStart = Boolean(params.snapToWeekStart);
    }

    return {
      enableSnapping: this.options.enableSnapping,
      snapUnit: this.options.snapUnit,
      snapStrength: this.options.snapStrength,
      snapFeedback: this.options.snapFeedback,
      snapToWeekStart: this.options.snapToWeekStart
    };
  }

  /**
   * ข้อมูลสำหรับป้องกันการส่งข้อมูลซ้ำซ้อน
   * @private
   */
  _updateLocks = {
    block: new Map(),
    marker: new Map(),
    connection: new Map(),
    blockUpdateInProgress: false,
    markerUpdateInProgress: false,
    connectionUpdateInProgress: false,
    updateMinInterval: 500
  };

  /**
   * ตรวจสอบว่าสามารถส่งข้อมูลบล็อกไปยัง API ได้หรือไม่
   * @param {string|number} blockId - รหัสบล็อก
   * @returns {boolean} สามารถส่งข้อมูลได้หรือไม่
   * @private
   */
  _canUpdateBlock(blockId) {
    if (this._updateLocks.blockUpdateInProgress) {
      return false;
    }

    const now = Date.now();
    const lastUpdate = this._updateLocks.block.get(blockId) || 0;

    return (now - lastUpdate) >= this._updateLocks.updateMinInterval;
  }

  /**
   * บันทึกเวลาที่ส่งข้อมูลบล็อกล่าสุด
   * @param {string|number} blockId - รหัสบล็อก
   * @param {boolean} isStart - เริ่มหรือสิ้นสุดการส่งข้อมูล
   * @private
   */
  _recordBlockUpdate(blockId, isStart = true) {
    if (isStart) {
      this._updateLocks.blockUpdateInProgress = true;
    } else {
      this._updateLocks.block.set(blockId, Date.now());
      this._updateLocks.blockUpdateInProgress = false;
    }
  }

  /**
   * ตรวจสอบว่าสามารถส่งข้อมูลมาร์คเกอร์ไปยัง API ได้หรือไม่
   * @param {string|number} markerId - รหัสมาร์คเกอร์
   * @returns {boolean} สามารถส่งข้อมูลได้หรือไม่
   * @private
   */
  _canUpdateMarker(markerId) {
    if (this._updateLocks.markerUpdateInProgress) {
      return false;
    }

    const now = Date.now();
    const lastUpdate = this._updateLocks.marker.get(markerId) || 0;

    return (now - lastUpdate) >= this._updateLocks.updateMinInterval;
  }

  /**
   * บันทึกเวลาที่ส่งข้อมูลมาร์คเกอร์ล่าสุด
   * @param {string|number} markerId - รหัสมาร์คเกอร์
   * @param {boolean} isStart - เริ่มหรือสิ้นสุดการส่งข้อมูล
   * @private
   */
  _recordMarkerUpdate(markerId, isStart = true) {
    if (isStart) {
      this._updateLocks.markerUpdateInProgress = true;
    } else {
      this._updateLocks.marker.set(markerId, Date.now());
      this._updateLocks.markerUpdateInProgress = false;
    }
  }

  /**
   * ตรวจสอบว่าสามารถส่งข้อมูลการเชื่อมโยงไปยัง API ได้หรือไม่
   * @param {string|number} connectionId - รหัสการเชื่อมโยง
   * @returns {boolean} สามารถส่งข้อมูลได้หรือไม่
   * @private
   */
  _canUpdateConnection(connectionId) {
    if (this._updateLocks.connectionUpdateInProgress) {
      return false;
    }

    const now = Date.now();
    const lastUpdate = this._updateLocks.connection.get(connectionId) || 0;

    return (now - lastUpdate) >= this._updateLocks.updateMinInterval;
  }

  /**
   * บันทึกเวลาที่ส่งข้อมูลการเชื่อมโยงล่าสุด
   * @param {string|number} connectionId - รหัสการเชื่อมโยง
   * @param {boolean} isStart - เริ่มหรือสิ้นสุดการส่งข้อมูล
   * @private
   */
  _recordConnectionUpdate(connectionId, isStart = true) {
    if (isStart) {
      this._updateLocks.connectionUpdateInProgress = true;
    } else {
      this._updateLocks.connection.set(connectionId, Date.now());
      this._updateLocks.connectionUpdateInProgress = false;
    }
  }

  /**
   * Retry sending pending updates when connection is restored
   * This should be called in appropriate places like after a successful API call
   */
  async checkAndRetryPendingUpdates() {
    if (this._pendingUpdates && this._pendingUpdates.length > 0) {
      return await this.retryPendingUpdates();
    }
    return true;
  }

  /**
   * Update markers from server data
   * @param {Array} updatedMarkers - Updated marker data from server
   * @private
   */
  updateMarkersFromServer(updatedMarkers) {
    if (!updatedMarkers || !Array.isArray(updatedMarkers) || updatedMarkers.length === 0) {
      return;
    }


    updatedMarkers.forEach(serverMarker => {
      const localMarker = this.markerManager.getMarker(serverMarker.id);

      if (localMarker) {
        localMarker.date = serverMarker.date;
        localMarker.text = serverMarker.text;
        localMarker.icon = serverMarker.icon;
        localMarker.color = serverMarker.color;

        if (serverMarker.row_index !== undefined && serverMarker.row_index !== localMarker.rowIndex) {
          localMarker.rowIndex = serverMarker.row_index;
        }
        if (serverMarker.start_date && serverMarker.start_date !== localMarker.startDate) {
          localMarker.startDate = serverMarker.start_date;
        }
        if (serverMarker.duration && serverMarker.duration !== localMarker.duration) {
          localMarker.duration = serverMarker.duration;
        }
      } else {
        this.markerManager.addMarker(serverMarker.id, serverMarker.date, serverMarker.text, serverMarker.icon, serverMarker.color);
      }
    });

    this.markerManager.renderMarkers();
  }

  /**
   * Handle failed server-side connection updates
   * @param {Object} blockData - Block data that was being updated
   * @param {Error|string} error - Error that occurred
   * @private
   */
  _handleConnectionUpdateError(blockData, error) {
    console.error('Failed to update connected blocks from server:', error);

    const blockId = blockData.id || blockData.tempId;

    if (this.dependencyManager && this.dependencyManager.connections) {
      const connectedToBlock = this.dependencyManager.connections.filter(
        conn => conn.fromId === blockId || conn.toId === blockId
      );
    }

    this.showNotification(
      this.t('Unable to update the linked blog Please look again or notify the system administrator.'),
      'error',
      5000
    );

    this.connectionManager.updateConnections();

    if (!this._connectionUpdateErrors) {
      this._connectionUpdateErrors = [];
    }

    this._connectionUpdateErrors.push({
      blockId: blockId,
      timestamp: Date.now(),
      error: error.toString()
    });

    if (this._connectionUpdateErrors.length >= 3) {
      const recentErrors = this._connectionUpdateErrors.filter(
        err => (Date.now() - err.timestamp) < 5 * 60 * 1000
      );

      if (recentErrors.length >= 3) {
        this.showNotification(
          this.t('There is a problem with connecting to the server to update the linked blog. Please record your work and refresh the webpage.'),
          'error',
          10000
        );

        this._connectionUpdateErrors = [];
      }
    }

    return false;
  }

  /**
   * ตั้งค่าโหมดการทำงานของ Timeline (Pure mode management without UI handling)
   * @param {string} mode - โหมดที่ต้องการ: 'normal', 'connection', 'marker', 'block'
   * @returns {boolean} - สถานะการเปลี่ยนโหมดสำเร็จหรือไม่
   */
  setMode(mode) {
    const oldMode = this.getCurrentMode();

    this._disableAllModes();

    switch (mode) {
      case 'normal':
        break;

      case 'connection':

        this.connectionManager.isConnectionMode = true;
        this.connectionManager.connectingFrom = null;

        if (!this.options.showAllConnections) {
          this.connectionManager.showAllConnections = true;
          this.connectionManager.updateVisibleConnections();
          this.connectionManager.updateConnections();
        }
        this.showNotification(
          this.t('Click on a source block, then click on a destination block to create a connection'),
          'info',
          3000
        );
        break;

      case 'marker':

        this.markerManager.isMarkerMode = true;
        this.showNotification(
          this.t('Click on the free space on Timeline to add a new marker'),
          'info',
          3000
        );
        break;

      case 'block':

        this.isClickToAddMode = true;
        this.options.allowClickToAdd = true;
        this.showNotification(
          this.t('Click on the free space on Timeline to add a new blog'),
          'info',
          3000
        );
        break;

      default:
        console.warn(`Unknown mode: ${mode}. Available modes: normal, connection, marker, block`);
        return false;
    }

    this.currentMode = mode;

    this.triggerCallback('onModeChange', {
      oldMode: oldMode,
      newMode: mode,
      timeline: this
    });

    return true;
  }

  /**
   * ปิดโหมดทั้งหมด (เฉพาะสถานะ ไม่มี UI handling)
   * @private
   */
  _disableAllModes() {
    this.connectionManager.isConnectionMode = false;
    this.connectionManager.connectingFrom = null;

    this.connectionManager.showAllConnections = this.options.showAllConnections;
    this.connectionManager.updateVisibleConnections();
    this.connectionManager.updateConnections();

    this.markerManager.isMarkerMode = false;

    this.isClickToAddMode = false;
    this.options.allowClickToAdd = false;
  }

  /**
   * ดึงโหมดปัจจุบันของ Timeline
   * @returns {string} - โหมดปัจจุบัน
   */
  getCurrentMode() {
    if (this.connectionManager.isConnectionMode) {
      return 'connection';
    } else if (this.markerManager.isMarkerMode) {
      return 'marker';
    } else if (this.isClickToAddMode) {
      return 'block';
    } else {
      return 'normal';
    }
  }

  /**
   * ตรวจสอบว่าอยู่ในโหมดที่ระบุหรือไม่
   * @param {string} mode - โหมดที่ต้องการตรวจสอบ
   * @returns {boolean} - true ถ้าอยู่ในโหมดที่ระบุ
   */
  isMode(mode) {
    return this.getCurrentMode() === mode;
  }

  /**
   * สลับโหมด - ถ้าอยู่ในโหมดที่ระบุจะเปลี่ยนเป็น normal, ถ้าไม่ใช่จะเปลี่ยนเป็นโหมดที่ระบุ
   * @param {string} mode - โหมดที่ต้องการสลับ
   * @returns {string} - โหมดใหม่หลังจากสลับ
   */
  toggleMode(mode) {
    const currentMode = this.getCurrentMode();
    const newMode = currentMode === mode ? 'normal' : mode;
    this.setMode(newMode);
    return newMode;
  }

  /**
   * Check for various empty states that prevent Timeline from rendering
   * @returns {Object|null} Empty state info or null if ready to render
   * @private
   */
  _checkEmptyState() {
    if (!this.currentProject) {
      return {
        type: 'no-project',
        title: this.t('No Project Loaded'),
        message: this.t('Please load a project to start working with the timeline.'),
        icon: '📁'
      };
    }

    if (!this.treeData || (Array.isArray(this.treeData) && this.treeData.length === 0)) {
      return {
        type: 'no-tree-data',
        title: this.t('No Project Structure'),
        message: this.t('Please create project structure (stages, areas, zones) to start adding timeline blocks.'),
        icon: '🌳',
        actionText: this.t('Create Structure'),
        actionCallback: () => this._triggerEmptyStateAction('create-structure')
      };
    }

    if (!this.workingDaysManager || !this.workingDaysManager.isInitialized()) {
      return {
        type: 'no-working-days',
        title: this.t('Working Days Not Configured'),
        message: this.t('Please configure working days to properly display the timeline.'),
        icon: '📅',
        actionText: this.t('Configure Working Days'),
        actionCallback: () => this._triggerEmptyStateAction('configure-working-days')
      };
    }

    if (!this.options.filterRange || !this.options.filterRange.start || !this.options.filterRange.end) {
      return {
        type: 'invalid-date-range',
        title: this.t('Invalid Date Range'),
        message: this.t('Please set a valid date range to display the timeline.'),
        icon: '📆',
        actionText: this.t('Set Date Range'),
        actionCallback: () => this._triggerEmptyStateAction('set-date-range')
      };
    }

    const container = document.getElementById(this.options.container);
    if (!container) {
      return {
        type: 'no-container',
        title: this.t('Container Not Found'),
        message: this.t('Timeline container element is missing from the page.'),
        icon: '⚠️'
      };
    }

    return null;
  }

  /**
   * Render empty state message in the timeline container
   * @param {Object} emptyState - Empty state information
   * @private
   */
  _renderEmptyState(emptyState) {
    const container = document.getElementById(this.options.container);
    if (!container) return;

    container.innerHTML = '';

    const emptyStateDiv = document.createElement('div');
    emptyStateDiv.className = 'timeline-empty-state';
    emptyStateDiv.innerHTML = `
      <div class="empty-state-content">
        <div class="empty-state-icon">${emptyState.icon}</div>
        <h3 class="empty-state-title">${emptyState.title}</h3>
        <p class="empty-state-message">${emptyState.message}</p>
        ${emptyState.actionText ? `
          <button class="empty-state-action btn btn-primary" data-action="${emptyState.type}">
            ${emptyState.actionText}
          </button>
        ` : ''}
      </div>
    `;

    container.appendChild(emptyStateDiv);

    const actionButton = emptyStateDiv.querySelector('.empty-state-action');
    if (actionButton && typeof emptyState.actionCallback === 'function') {
      actionButton.addEventListener('click', () => emptyState.actionCallback());
    }

    if (this.uiCallbacks.onEmptyState) {
      this.uiCallbacks.onEmptyState(emptyState);
    }
  }

  /**
   * Trigger empty state action callback
   * @param {string} action - Action type
   * @private
   */
  _triggerEmptyStateAction(action) {
    if (this.uiCallbacks.onEmptyState) {
      this.uiCallbacks.onEmptyState({
        type: 'action',
        action: action,
        timeline: this
      });
    }
  }

  /**
   * Default notification handler (fallback when no callback provided)
   * @param {string} message - Notification message
   * @param {string} type - Notification type
   * @param {number} duration - Duration in milliseconds
   * @private
   */
  _defaultShowNotification(message, type = 'info', duration = 3000) {
    if (this.language) {
      const translated = this.language.translate(message);
      if (translated !== message) {
        message = translated;
      }
    }


    const existingNotifications = document.querySelectorAll('.notification');
    existingNotifications.forEach(note => {
      note.classList.remove('show');
      setTimeout(() => note.remove(), 300);
    });

    const notification = document.createElement('div');
    notification.className = `notification ${type} show`;
    notification.innerHTML = message;

    switch (type) {
      case 'error':
        notification.style.backgroundColor = '#ff4d4d';
        break;
      case 'warning':
        notification.style.backgroundColor = '#ff9f40';
        break;
      case 'info':
        notification.style.backgroundColor = '#4a6fff';
        break;
      case 'success':
      default:
        notification.style.backgroundColor = '#47c363';
    }

    document.body.appendChild(notification);

    notification.classList.add('show');

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, duration);

    if (type === 'error') {
      console.error(message);
    } else if (type === 'warning') {
      console.warn(message);
    }
  }
}

window.TimelineCalendar = TimelineCalendar;
